import React from 'react';
import { ArrowRight, Heart, Award, Users, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Home = () => {
  return (
    <div className="min-h-screen bg-background-light flex flex-col">
      {/* Navigation Bar */}
      <nav className="bg-white shadow-sm py-4 px-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-primary font-bold text-2xl">Stepping4Life</span>
          </div>
          <div className="hidden md:flex space-x-6 items-center">
            <a href="#features" className="text-text-secondary hover:text-primary transition-colors">Features</a>
            <a href="#how-it-works" className="text-text-secondary hover:text-primary transition-colors">How It Works</a>
            <a href="#testimonials" className="text-text-secondary hover:text-primary transition-colors">Testimonials</a>
            <Link to="/login" className="text-secondary font-medium">Log In</Link>
            <Link to="/signup" className="bg-primary text-white px-4 py-2 rounded-md hover:bg-opacity-90 transition-all">Sign Up</Link>
          </div>
          <button className="md:hidden text-text-primary">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 mb-8 md:mb-0">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Step Into A Healthier Lifestyle</h1>
              <p className="text-lg md:text-xl mb-8 text-white text-opacity-90">
                Track your steps, join walking challenges, and connect with a community of walkers. Your journey to better health begins with a single step.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/signup" className="bg-accent text-white px-6 py-3 rounded-md font-medium text-center hover:bg-opacity-90 transition-all flex items-center justify-center">
                  Get Started <ArrowRight size={18} className="ml-2" />
                </Link>
                <Link to="/login" className="bg-white bg-opacity-20 text-white px-6 py-3 rounded-md font-medium text-center hover:bg-opacity-30 transition-all">
                  Log In
                </Link>
              </div>
            </div>
            <div className="md:w-1/2 flex justify-center">
              <img 
                src="workout-female.png" 
                alt="Person walking with fitness tracker" 
                className="rounded-lg shadow-lg max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section id="features" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">Why Choose Stepping4Life?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Feature 1 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-primary bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Heart size={24} className="text-primary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Personalized Health Insights</h3>
              <p className="text-text-secondary">
                Receive customized recommendations based on your walking patterns, fitness level, and health goals.
              </p>
            </div>
            
            {/* Feature 2 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-secondary bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Award size={24} className="text-secondary" />
              </div>
              <h3 className="text-xl font-bold mb-3">Fun Challenges & Goals</h3>
              <p className="text-text-secondary">
                Join individual or group challenges to stay motivated and transform walking into an exciting activity.
              </p>
            </div>
            
            {/* Feature 3 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
              <div className="bg-accent bg-opacity-10 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                <Users size={24} className="text-accent" />
              </div>
              <h3 className="text-xl font-bold mb-3">Supportive Community</h3>
              <p className="text-text-secondary">
                Connect with like-minded walkers, share achievements, and motivate each other on your fitness journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-background-light">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-4">How It Works</h2>
          <p className="text-text-secondary text-center max-w-2xl mx-auto mb-12">
            Getting started with Stepping4Life is easy. Just follow these simple steps and begin your walking journey today.
          </p>
          
          <div className="flex flex-col md:flex-row gap-8">
            {/* Step 1 */}
            <div className="flex-1 relative">
              <div className="bg-white rounded-xl p-6 shadow-sm h-full relative z-10">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-3">Create Your Profile</h3>
                <p className="text-text-secondary">
                  Sign up and complete your profile with your fitness level, health information, and walking preferences.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-16 h-1 bg-primary transform -translate-y-1/2 -translate-x-8 z-0"></div>
            </div>
            
            {/* Step 2 */}
            <div className="flex-1 relative">
              <div className="bg-white rounded-xl p-6 shadow-sm h-full relative z-10">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-3">Set Your Goals</h3>
                <p className="text-text-secondary">
                  Define personalized daily and weekly step goals based on your fitness level and health objectives.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-16 h-1 bg-primary transform -translate-y-1/2 -translate-x-8 z-0"></div>
            </div>
            
            {/* Step 3 */}
            <div className="flex-1 relative">
              <div className="bg-white rounded-xl p-6 shadow-sm h-full relative z-10">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-3">Track Your Progress</h3>
                <p className="text-text-secondary">
                  Connect your wearable device or use your phone to automatically track your daily steps and activities.
                </p>
              </div>
              <div className="hidden md:block absolute top-1/2 left-full w-16 h-1 bg-primary transform -translate-y-1/2 -translate-x-8 z-0"></div>
            </div>
            
            {/* Step 4 */}
            <div className="flex-1">
              <div className="bg-white rounded-xl p-6 shadow-sm h-full">
                <div className="bg-primary text-white rounded-full w-8 h-8 flex items-center justify-center text-lg font-bold mb-4">
                  4
                </div>
                <h3 className="text-xl font-bold mb-3">Join Challenges</h3>
                <p className="text-text-secondary">
                  Participate in solo or group challenges to keep motivated and celebrate achievements together.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold text-center mb-12">What Our Users Say</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <span className="text-lg font-bold text-gray-400">JD</span>
                </div>
                <div>
                  <h4 className="font-bold">Jane Doe</h4>
                  <p className="text-sm text-text-secondary">Walking for 6 months</p>
                </div>
              </div>
              <p className="text-text-secondary italic">
                "Stepping4Life has completely changed my walking routine. The challenges keep me motivated and I've met amazing people through the community."
              </p>
              <div className="flex mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            
            {/* Testimonial 2 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <span className="text-lg font-bold text-gray-400">JS</span>
                </div>
                <div>
                  <h4 className="font-bold">John Smith</h4>
                  <p className="text-sm text-text-secondary">Walking for 3 months</p>
                </div>
              </div>
              <p className="text-text-secondary italic">
                "I've lost 15 pounds since I started using Stepping4Life. The personalized recommendations really helped me optimize my walking routine."
              </p>
              <div className="flex mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
            
            {/* Testimonial 3 */}
            <div className="bg-background-light rounded-xl p-6 shadow-sm">
              <div className="flex items-center mb-4">
                <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                  <span className="text-lg font-bold text-gray-400">AL</span>
                </div>
                <div>
                  <h4 className="font-bold">Amanda Lee</h4>
                  <p className="text-sm text-text-secondary">Walking for 1 year</p>
                </div>
              </div>
              <p className="text-text-secondary italic">
                "The map feature has helped me discover amazing walking routes in my neighborhood I never knew existed. I look forward to my daily walks now!"
              </p>
              <div className="flex mt-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg key={star} xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-accent" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-secondary py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to start your walking journey?</h2>
          <p className="text-white text-opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of users who have transformed their health with just a few steps a day. Get started with Stepping4Life today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="bg-accent text-white px-6 py-3 rounded-md font-medium hover:bg-opacity-90 transition-all inline-flex items-center justify-center">
              Sign Up Free <ArrowRight size={18} className="ml-2" />
            </Link>
            <Link to="/login" className="bg-white text-secondary px-6 py-3 rounded-md font-medium hover:bg-opacity-90 transition-all inline-flex items-center justify-center">
              Log In <ArrowUpRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-text-primary py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <span className="text-white font-bold text-xl mb-4 block">Stepping4Life</span>
              <p className="text-white text-opacity-70 text-sm">
                Your journey to better health begins with a single step.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">App</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Features</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">How It Works</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Testimonials</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Pricing</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Help Center</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Contact Us</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Privacy Policy</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Terms of Service</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-bold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Twitter</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Facebook</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">Instagram</a></li>
                <li><a href="#" className="text-white text-opacity-70 hover:text-opacity-100 transition-opacity text-sm">YouTube</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white border-opacity-20 pt-8 text-center">
            <p className="text-white text-opacity-70 text-sm">
              © {new Date().getFullYear()} Stepping4Life. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;